/**
 * Minimal IndexedDB store for POS offline — plan/28.
 * No third-party IDB wrapper; Capacitor SQLite can replace later.
 */

import type { OutboxItem, Watermark } from "./sync-math";
import type { PosCatalogItem, PosSession } from "@/shared/api/pos";
import type { SyncParty, SyncPayMethod } from "@/shared/api/sync";

const DB_NAME = "icraftx-pos";
const DB_VERSION = 4;

export type CatalogCache = {
  companyRef: string;
  terminalId: string;
  items: PosCatalogItem[];
  watermark: Watermark;
  savedAt: string;
};

export type SessionCache = {
  companyRef: string;
  terminalId: string;
  session: PosSession;
  savedAt: string;
};

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error("IDB open failed"));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("catalog")) {
        db.createObjectStore("catalog", { keyPath: "key" });
      }
      if (!db.objectStoreNames.contains("parties")) {
        db.createObjectStore("parties", { keyPath: "key" });
      }
      if (!db.objectStoreNames.contains("payMethods")) {
        db.createObjectStore("payMethods", { keyPath: "key" });
      }
      if (!db.objectStoreNames.contains("outbox")) {
        const store = db.createObjectStore("outbox", { keyPath: "id" });
        store.createIndex("byTerminal", "terminalId", { unique: false });
        store.createIndex("bySeq", "seq", { unique: false });
      }
      if (!db.objectStoreNames.contains("meta")) {
        db.createObjectStore("meta", { keyPath: "key" });
      }
      if (!db.objectStoreNames.contains("cashiers")) {
        db.createObjectStore("cashiers", { keyPath: "key" });
      }
      if (!db.objectStoreNames.contains("ticketReturns")) {
        db.createObjectStore("ticketReturns", { keyPath: "ticketId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

/** Cached POS ticket lines for offline return against synced sales — plan/28. */
export type TicketReturnCache = {
  ticketId: string;
  terminalId: string;
  number: string;
  warehouseId?: string | null;
  lines: Array<{
    sourceLineId: string;
    productId: string;
    qty: string;
    lotCode?: string | null;
    serialCode?: string | null;
  }>;
  savedAt: string;
};

export type CachedCashier = {
  companyRef: string;
  terminalId: string;
  userCode: string;
  userId: string;
  displayName: string;
  saltHex: string;
  verifierHex: string;
  /** Action / allow keys for this user — plan/28 offline soft limits */
  permissionKeys?: string[];
  /** Keys this user may approve (implies can_approve) — plan/14 on-device PIN */
  canApproveKeys?: string[];
  savedAt: string;
};

function cashierKey(companyRef: string, terminalId: string, userCode: string) {
  return `${companyRef}::${terminalId}::${userCode}`;
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("IDB tx failed"));
    tx.onabort = () => reject(tx.error ?? new Error("IDB tx aborted"));
  });
}

function catalogKey(companyRef: string, terminalId: string): string {
  return `${companyRef}::${terminalId}`;
}

export async function saveCatalogCache(cache: CatalogCache): Promise<void> {
  const db = await openDb();
  const tx = db.transaction("catalog", "readwrite");
  tx.objectStore("catalog").put({
    key: catalogKey(cache.companyRef, cache.terminalId),
    ...cache,
  });
  await txDone(tx);
  db.close();
}

export async function loadCatalogCache(
  companyRef: string,
  terminalId: string,
): Promise<CatalogCache | null> {
  const db = await openDb();
  const tx = db.transaction("catalog", "readonly");
  const req = tx.objectStore("catalog").get(catalogKey(companyRef, terminalId));
  const row = await new Promise<CatalogCache | undefined>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result as CatalogCache | undefined);
    req.onerror = () => reject(req.error);
  });
  await txDone(tx);
  db.close();
  return row ?? null;
}

export async function nextOutboxSeq(terminalId: string): Promise<number> {
  const db = await openDb();
  const tx = db.transaction("meta", "readwrite");
  const key = `seq::${terminalId}`;
  const store = tx.objectStore("meta");
  const current = await new Promise<number>((resolve, reject) => {
    const req = store.get(key);
    req.onsuccess = () => {
      const v = req.result as { key: string; value: number } | undefined;
      resolve(v?.value ?? 0);
    };
    req.onerror = () => reject(req.error);
  });
  const next = current + 1;
  store.put({ key, value: next });
  await txDone(tx);
  db.close();
  return next;
}

export async function enqueueOutbox(
  item: Omit<OutboxItem, "seq"> & { seq?: number },
): Promise<OutboxItem> {
  const seq = item.seq ?? (await nextOutboxSeq(item.terminalId));
  const full: OutboxItem = { ...item, seq };
  const db = await openDb();
  const tx = db.transaction("outbox", "readwrite");
  tx.objectStore("outbox").put(full);
  await txDone(tx);
  db.close();
  return full;
}

export async function listOutbox(terminalId?: string): Promise<OutboxItem[]> {
  const db = await openDb();
  const tx = db.transaction("outbox", "readonly");
  const store = tx.objectStore("outbox");
  const items = await new Promise<OutboxItem[]>((resolve, reject) => {
    if (!terminalId) {
      const req = store.getAll();
      req.onsuccess = () => resolve((req.result as OutboxItem[]) ?? []);
      req.onerror = () => reject(req.error);
      return;
    }
    const idx = store.index("byTerminal");
    const req = idx.getAll(terminalId);
    req.onsuccess = () => resolve((req.result as OutboxItem[]) ?? []);
    req.onerror = () => reject(req.error);
  });
  await txDone(tx);
  db.close();
  return items;
}

export async function removeOutbox(id: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction("outbox", "readwrite");
  tx.objectStore("outbox").delete(id);
  await txDone(tx);
  db.close();
}

export async function updateOutbox(item: OutboxItem): Promise<void> {
  const db = await openDb();
  const tx = db.transaction("outbox", "readwrite");
  tx.objectStore("outbox").put(item);
  await txDone(tx);
  db.close();
}

export async function pendingCount(terminalId?: string): Promise<number> {
  const items = await listOutbox(terminalId);
  return items.filter((i) => i.syncStatus !== "conflict").length;
}

export async function conflictCount(terminalId?: string): Promise<number> {
  const items = await listOutbox(terminalId);
  return items.filter((i) => i.syncStatus === "conflict").length;
}

export async function listConflictOutbox(
  terminalId: string,
): Promise<OutboxItem[]> {
  const items = await listOutbox(terminalId);
  return items.filter((i) => i.syncStatus === "conflict");
}

function sessionKey(companyRef: string, terminalId: string): string {
  return `session::${companyRef}::${terminalId}`;
}

export async function saveSessionCache(cache: SessionCache): Promise<void> {
  const db = await openDb();
  const tx = db.transaction("meta", "readwrite");
  tx.objectStore("meta").put({
    key: sessionKey(cache.companyRef, cache.terminalId),
    value: cache,
  });
  await txDone(tx);
  db.close();
}

export async function loadSessionCache(
  companyRef: string,
  terminalId: string,
): Promise<SessionCache | null> {
  const db = await openDb();
  const tx = db.transaction("meta", "readonly");
  const req = tx.objectStore("meta").get(sessionKey(companyRef, terminalId));
  const row = await new Promise<
    { key: string; value: SessionCache } | undefined
  >((resolve, reject) => {
    req.onsuccess = () =>
      resolve(req.result as { key: string; value: SessionCache } | undefined);
    req.onerror = () => reject(req.error);
  });
  await txDone(tx);
  db.close();
  return row?.value ?? null;
}

export type PartiesCache = {
  companyRef: string;
  parties: SyncParty[];
  watermark: Watermark;
  savedAt: string;
};

export type PayMethodsCache = {
  companyRef: string;
  methods: SyncPayMethod[];
  watermark: Watermark;
  savedAt: string;
};

export async function savePartiesCache(cache: PartiesCache): Promise<void> {
  const db = await openDb();
  const tx = db.transaction("parties", "readwrite");
  tx.objectStore("parties").put({ key: cache.companyRef, ...cache });
  await txDone(tx);
  db.close();
}

export async function loadPartiesCache(
  companyRef: string,
): Promise<PartiesCache | null> {
  const db = await openDb();
  const tx = db.transaction("parties", "readonly");
  const req = tx.objectStore("parties").get(companyRef);
  const row = await new Promise<PartiesCache | undefined>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result as PartiesCache | undefined);
    req.onerror = () => reject(req.error);
  });
  await txDone(tx);
  db.close();
  return row ?? null;
}

export async function savePayMethodsCache(
  cache: PayMethodsCache,
): Promise<void> {
  const db = await openDb();
  const tx = db.transaction("payMethods", "readwrite");
  tx.objectStore("payMethods").put({ key: cache.companyRef, ...cache });
  await txDone(tx);
  db.close();
}

export async function loadPayMethodsCache(
  companyRef: string,
): Promise<PayMethodsCache | null> {
  const db = await openDb();
  const tx = db.transaction("payMethods", "readonly");
  const req = tx.objectStore("payMethods").get(companyRef);
  const row = await new Promise<PayMethodsCache | undefined>(
    (resolve, reject) => {
      req.onsuccess = () => resolve(req.result as PayMethodsCache | undefined);
      req.onerror = () => reject(req.error);
    },
  );
  await txDone(tx);
  db.close();
  return row ?? null;
}

export async function saveCachedCashier(row: CachedCashier): Promise<void> {
  const db = await openDb();
  const tx = db.transaction("cashiers", "readwrite");
  tx.objectStore("cashiers").put({
    key: cashierKey(row.companyRef, row.terminalId, row.userCode),
    ...row,
  });
  await txDone(tx);
  db.close();
}

export async function loadCachedCashier(
  companyRef: string,
  terminalId: string,
  userCode: string,
): Promise<CachedCashier | null> {
  const db = await openDb();
  const tx = db.transaction("cashiers", "readonly");
  const req = tx
    .objectStore("cashiers")
    .get(cashierKey(companyRef, terminalId, userCode));
  const row = await new Promise<(CachedCashier & { key: string }) | undefined>(
    (resolve, reject) => {
      req.onsuccess = () =>
        resolve(req.result as (CachedCashier & { key: string }) | undefined);
      req.onerror = () => reject(req.error);
    },
  );
  await txDone(tx);
  db.close();
  return row ?? null;
}

/** All PIN-cached users for a terminal (cashiers + managers who approved on-device). */
export async function listCachedCashiers(
  companyRef: string,
  terminalId: string,
): Promise<CachedCashier[]> {
  const db = await openDb();
  const tx = db.transaction("cashiers", "readonly");
  const req = tx.objectStore("cashiers").getAll();
  const rows = await new Promise<Array<CachedCashier & { key: string }>>(
    (resolve, reject) => {
      req.onsuccess = () =>
        resolve((req.result as Array<CachedCashier & { key: string }>) ?? []);
      req.onerror = () => reject(req.error);
    },
  );
  await txDone(tx);
  db.close();
  const prefix = `${companyRef}::${terminalId}::`;
  return rows.filter((r) => r.key.startsWith(prefix));
}

export async function saveTicketReturnCache(
  cache: TicketReturnCache,
): Promise<void> {
  const db = await openDb();
  const tx = db.transaction("ticketReturns", "readwrite");
  tx.objectStore("ticketReturns").put(cache);
  await txDone(tx);
  db.close();
}

export async function loadTicketReturnCache(
  ticketId: string,
): Promise<TicketReturnCache | null> {
  const db = await openDb();
  const tx = db.transaction("ticketReturns", "readonly");
  const req = tx.objectStore("ticketReturns").get(ticketId);
  const row = await new Promise<TicketReturnCache | undefined>(
    (resolve, reject) => {
      req.onsuccess = () => resolve(req.result as TicketReturnCache | undefined);
      req.onerror = () => reject(req.error);
    },
  );
  await txDone(tx);
  db.close();
  return row ?? null;
}

export async function removeTicketReturnCache(ticketId: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction("ticketReturns", "readwrite");
  tx.objectStore("ticketReturns").delete(ticketId);
  await txDone(tx);
  db.close();
}
